import React from "react";
import DateOption from "./DateOption";
import InputDate from "./InputDate";
import ChildrenButton from "./ChildrenButton";
import NumberInput from "./NumberInput";
import CongesMaternite from "./CongesMaternite";
import { MAX_ADVANCE, MAX_REPORT, clampShift, computeLeave, dueDateFrom, isPlausible, situationKey } from "./congeRules";

function shiftOptions(key) {
    const options = [];
    for (let w = MAX_ADVANCE[key]; w >= 1; w--) {
        options.push({ value: -w, label: `Commencer ${w} semaine${w > 1 ? "s" : ""} plus tôt` });
    }
    options.push({ value: 0, label: "Dates standards" });
    for (let w = 1; w <= MAX_REPORT; w++) {
        options.push({ value: w, label: `Reporter ${w} semaine${w > 1 ? "s" : ""} après la naissance` });
    }
    return options;
}

export default function CongeMaterniteApp() {
    const [inputDate, setInputDate] = React.useState("");
    const [dateOption, setDateOption] = React.useState("startPregnancy");
    const [children, setChildren] = React.useState(0);
    const [babies, setBabies] = React.useState(1);
    const [shift, setShift] = React.useState(0);
    const [pathoPre, setPathoPre] = React.useState(false);
    const [pathoPost, setPathoPost] = React.useState(false);
    const [salary, setSalary] = React.useState("");

    const key = situationKey(children, babies);
    const appliedShift = clampShift(key, shift);

    // Parsed as local midnight: new Date("yyyy-mm-dd") would be UTC and can land
    // on the previous day in negative offsets.
    const parsed = inputDate ? new Date(`${inputDate}T00:00:00`) : null;
    const leave = isPlausible(parsed)
        ? computeLeave({ dueDate: dueDateFrom(dateOption, parsed), childrenBorn: children, babies, shift: appliedShift, pathoPre, pathoPost })
        : null;

    return (
        <>
            <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg flex flex-col gap-3">
                <DateOption dateOption={dateOption} handleDateOptionChange={(e) => setDateOption(e.target.value)} />
                <InputDate dateOption={dateOption} inputDate={inputDate} handleInputDateChange={(e) => setInputDate(e.target.value)} />

                <div className="flex gap-4 items-center">
                    <p className="shrink-0 text-sm">Enfant(s) déjà né(s)</p>
                    <div className="w-[100px]">
                        <NumberInput children={children} setChildren={setChildren} />
                    </div>
                </div>

                <div>
                    <p className="text-sm mb-2">Vous attendez</p>
                    <div className="flex gap-2 sm:gap-4 items-stretch">
                        <ChildrenButton number={1} onClick={setBabies} isSelected={babies === 1} />
                        <ChildrenButton number={2} onClick={setBabies} isSelected={babies === 2} />
                        <ChildrenButton number={3} onClick={setBabies} isSelected={babies >= 3} />
                    </div>
                </div>

                <details className="text-sm">
                    <summary className="cursor-pointer font-semibold">Options : report, congé pathologique, indemnités</summary>
                    <div className="flex flex-col gap-3 mt-3">
                        <label className="flex flex-col gap-1">
                            <span>Répartition avant / après la naissance</span>
                            <select className="dropdown-filter select" value={appliedShift} onChange={(e) => setShift(Number(e.target.value))}>
                                {shiftOptions(key).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                            <span className="text-xs text-gray-600">Sur avis favorable de votre médecin ou sage-femme.</span>
                        </label>
                        <label className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1" checked={pathoPre} onChange={(e) => setPathoPre(e.target.checked)} />
                            <span>Congé pathologique prénatal (2 semaines, sur prescription)</span>
                        </label>
                        <label className="flex items-start gap-2">
                            <input type="checkbox" className="mt-1" checked={pathoPost} onChange={(e) => setPathoPost(e.target.checked)} />
                            <span>Congé pathologique postnatal (jusqu'à 4 semaines, sur prescription)</span>
                        </label>
                        <label className="flex flex-col gap-1">
                            <span>Salaire brut mensuel moyen des 3 derniers mois (€)</span>
                            <input
                                type="number"
                                inputMode="decimal"
                                min="0"
                                step="1"
                                className="dropdown min-w-32 sm:max-w-56"
                                value={salary}
                                onChange={(e) => setSalary(e.target.value)}
                                placeholder="ex. 2500"
                            />
                        </label>
                    </div>
                </details>
            </div>

            <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
                Résultats
            </div>

            <div className="p-4 my-2 sm:my-4 bg-teal-700 rounded-lg">
                <CongesMaternite leave={leave} salary={Number(salary)} />
            </div>
        </>
    );
}
